import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Input } from 'src/components/UI/Forms/Inputs';
import FileUpload from 'src/components/UI/Forms/Inputs/fileUpload';
import DatePickerComponent from 'src/components/UI/Forms/Inputs/datePicker';
import PdfViewer from 'src/components/UI/PdfViewer';
import CustomSnackbar from 'src/components/UI/Alerts/customSnackbar';
import { Button, Divider } from '@heroui/react';

import {
  IoSaveOutline,
  CiEdit,
  CiUser,
  MdOutlineCancel,
  BsBuildings,
  TbLicense,
  LuCalendarClock,
  TbFileCertificate,
  MdLocalPhone,
  IoMailOutline,
  GrLanguage,
} from 'src/icons';

import {
  useGetUserProfileDataQuery,
  useGetCompanyByIdQuery,
  useUpdateCompanyByIdMutation,
} from 'src/services/user/user';
import { getUser } from 'src/utils/user/user';

interface ProfileFormValues {
  companyName?: string;
  tradeLicenseNumber?: string;
  trnNumber?: string;
}

export default function Profile() {
  // Fetch basic user profile (which includes the company _id)
  const { data: userProfileData, refetch: refetchUserProfile } =
    useGetUserProfileDataQuery();
  const companyId = userProfileData?.company?._id;
  const userType = getUser().type;

  // Fetch detailed company data using the companyId (skip until available)
  const { data: companyData, refetch: refetchCompany } = useGetCompanyByIdQuery(
    companyId!,
    {
      skip: !companyId,
    }
  );

  // RTK Query mutation hook for updating company details
  const [updateCompany] = useUpdateCompanyByIdMutation();

  // React Hook Form setup
  const { register, reset, getValues } = useForm<ProfileFormValues>();
  const { t, i18n } = useTranslation();

  // Snackbar state for user notifications
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');

  // Edit mode state
  const [editState, setEditState] = useState(false);

  // File upload states
  const [tlFile, setTlFile] = useState<File | null>(null);
  const [siraCertFile, setSiraCertFile] = useState<File | null>(null);
  const [commRegFile, setCommRegFile] = useState<File | null>(null);
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);

  // Date states for expiry fields
  const [tradeLicenseExpiry, setTradeLicenseExpiry] = useState<Date | null>(
    null
  );
  const [siraCertExpiry, setSiraCertExpiry] = useState<Date | null>(null);
  const [commRegExpiry, setCommRegExpiry] = useState<Date | null>(null);
  const [insuranceExpiry, setInsuranceExpiry] = useState<Date | null>(null);
  const [isDateError] = useState(false);

  // Memoized language options for language selection
  const lngs = useMemo(
    () => ({
      en: { nativeName: '🇬🇧 EN' },
      ar: { nativeName: '🇦🇪 AR' },
    }),
    []
  );

  // Initialize form and date states when detailed companyData is available
  useEffect(() => {
    if (companyData) {
      reset({
        companyName: companyData.name,
        tradeLicenseNumber: companyData.tradeLicenseNumber,
        trnNumber: companyData.trnNumber,
      });
      setTradeLicenseExpiry(
        companyData.tradeLicenseExpiry
          ? new Date(companyData.tradeLicenseExpiry)
          : null
      );
      setSiraCertExpiry(
        companyData.siraCertExpiry ? new Date(companyData.siraCertExpiry) : null
      );
      setCommRegExpiry(
        companyData.commRegExpiry ? new Date(companyData.commRegExpiry) : null
      );
      setInsuranceExpiry(
        companyData.insuranceExpiry
          ? new Date(companyData.insuranceExpiry)
          : null
      );
    }
  }, [companyData, reset]);

  // Handle file input changes
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, fileType: string) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        switch (fileType) {
          case 'tlFile':
            setTlFile(file);
            break;
          case 'siraCertFile':
            setSiraCertFile(file);
            break;
          case 'commRegFile':
            setCommRegFile(file);
            break;
          case 'insuranceFile':
            setInsuranceFile(file);
            break;
          default:
            console.warn('Unknown file type:', fileType);
        }
      }
    },
    []
  );

  // Save handler using useUpdateCompanyByIdMutation with Moment.js for date formatting
  const handleSave = useCallback(async () => {
    const formValues = getValues();


    const submitData = new FormData();

    if (formValues.tradeLicenseNumber) {
      submitData.append('tradeLicenseNumber', formValues.tradeLicenseNumber);
    }
    if (tlFile) {
      submitData.append('tlFile', tlFile);
    }
    if (siraCertFile) {
      submitData.append('siraCertFile', siraCertFile);
    }
    if (commRegFile) {
      submitData.append('commRegFile', commRegFile);
    }
    if (insuranceFile) {
      submitData.append('insuranceFile', insuranceFile);
    }
    if (formValues.trnNumber) {
      submitData.append('trnNumber', formValues.trnNumber);
    }
    if (tradeLicenseExpiry) {
      submitData.append(
        'tradeLicenseExpiry',
        moment(tradeLicenseExpiry).format('YYYY-MM-DD')
      );
    }
    if (siraCertExpiry) {
      submitData.append(
        'siraCertExpiry',
        moment(siraCertExpiry).format('YYYY-MM-DD')
      );
    }
    if (commRegExpiry) {
      submitData.append(
        'commRegExpiry',
        moment(commRegExpiry).format('YYYY-MM-DD')
      );
    }
    if (insuranceExpiry) {
      submitData.append(
        'insuranceExpiry',
        moment(insuranceExpiry).format('YYYY-MM-DD')
      );
    }

    try {
      await updateCompany({ id: companyId, data: submitData }).unwrap();
      refetchCompany();
      await refetchUserProfile();
      setSnackbarMessage('Data updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Error updating company data:', error);
      setSnackbarMessage('Something went wrong! Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
    setEditState(false);
  }, [
    getValues,
    tlFile,
    siraCertFile,
    commRegFile,
    insuranceFile,
    tradeLicenseExpiry,
    siraCertExpiry,
    commRegExpiry,
    insuranceExpiry,
    companyId,
    refetchCompany,
    refetchUserProfile,
    updateCompany,
  ]);

  // Cancel handler resets form values to the fetched company data
  const handleCancel = useCallback(() => {
    if (companyData) {
      reset({
        companyName: companyData.name,
        tradeLicenseNumber: companyData.tradeLicenseNumber,
        trnNumber: companyData.trnNumber,
      });
      setTradeLicenseExpiry(
        companyData.tradeLicenseExpiry
          ? new Date(companyData.tradeLicenseExpiry)
          : null
      );
      setSiraCertExpiry(
        companyData.siraCertExpiry ? new Date(companyData.siraCertExpiry) : null
      );
      setCommRegExpiry(
        companyData.commRegExpiry ? new Date(companyData.commRegExpiry) : null
      );
      setInsuranceExpiry(
        companyData.insuranceExpiry
          ? new Date(companyData.insuranceExpiry)
          : null
      );
    }
    setEditState(false);
  }, [companyData, reset]);

  // Language change handler
  const changeLanguage = useCallback(
    async (toLang: string) => {
      localStorage.setItem('selectedLanguage', toLang);
      await i18n.changeLanguage(toLang);
      document.documentElement.dir = toLang === 'ar' ? 'rtl' : 'ltr';
    },
    [i18n]
  );

  return (
    <>
      <div className="w-full h-screen px-10 overflow-y-auto" style={{ backgroundColor: "var(--bg-base)", color: "var(--text-main)" }}>
        <div className="sticky top-0 z-10 py-8 pb-4" style={{ backgroundColor: "var(--bg-base)" }}>
          <div className="flex justify-between py-4">
            <div className="flex items-center">
              <h3 className="text-2xl">{t('profile_page.title')}</h3>
            </div>
            <div>
              {userType === 'shipper' && (
                <>
                  {editState ? (
                    <>
                      <Button
                        variant="solid"
                        className="rounded p-2 ml-4 shadow-sm font-medium bg-success text-white"
                        onPress={handleSave}
                        startContent={<IoSaveOutline className="w-5 h-5" />}
                      >
                        Update
                      </Button>
                      <Button
                        variant="solid"
                        className="ml-4 rounded p-2 shadow-sm font-medium" style={{ backgroundColor: "var(--bg-overlay)", color: "var(--text-main)" }}
                        onPress={handleCancel}
                        startContent={<MdOutlineCancel className="w-5 h-5" />}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="solid"
                      className="flex items-center rounded p-2 shadow-sm font-medium transition-colors duration-200" style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
                      onPress={() => setEditState(true)}
                      startContent={<CiEdit className="w-5 h-5" />}
                    >
                      {t('button.edit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <hr className="opacity-10" />
        </div>
        <section className="mb-24">
          <form>
            {/* Full Name - only show if available */}
            {(userProfileData?.firstName || userProfileData?.lastName) && (
              <>
                <div className="grid grid-cols-4 my-4">
                  <div className="my-auto">
                    <CiUser className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                    <p className="mb-4 text-md">{t('profile_page.full_name') || 'Full Name'}</p>
                  </div>
                  <div className="col-span-1 my-auto">
                    <Input
                      type="text"
                      name="fullName"
                      inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]"
                      defaultValue={`${userProfileData?.firstName ?? ''} ${userProfileData?.lastName ?? ''}`.trim()}
                      register={register}
                      disabled
                    />
                  </div>
                </div>
                <Divider className="opacity-10" />
              </>
            )}

            {/* Agency Name - only show if available */}
            {companyData?.name && (
              <>
                <div className="grid grid-cols-4 my-4">
                  <div className="my-auto">
                    <BsBuildings className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                    <p className="mb-4 text-md">{t('profile_page.agency_name')}</p>
                  </div>
                  <div className="col-span-1 my-auto">
                    <Input
                      type="text"
                      name="companyName"
                      inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]"
                      defaultValue={companyData?.name ?? ''}
                      register={register}
                      disabled
                    />
                  </div>
                </div>
                <Divider className="opacity-10" />
              </>
            )}

            {userType === 'shipper' && (
              <>
                {/* Trade License Section */}
                <div className="my-6">
                  <div className="grid grid-cols-4 mt-8 mb-4">
                    <div className="my-auto">
                      <TbLicense className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">Trade License Number</p>
                    </div>
                    <div className="col-span-1 my-auto">
                      <Input
                        type="text"
                        name="tradeLicenseNumber"
                        placeholder="e.g. TLN-123456789"
                        inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]"
                        defaultValue={companyData?.tradeLicenseNumber ?? ''}
                        register={register}
                        disabled={!editState}
                      />
                    </div>
                  </div>
                </div>

                <div className="my-2">
                  <div className="grid grid-cols-4 mb-6">
                    <div className="my-auto" />
                    <div className="col-span-1 my-auto">
                      {editState ? (
                        <FileUpload
                          id="tlFile"
                          name="tlFile"
                          inputClassName="border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] !text-[var(--text-main)] !text-sm shadow-sm"
                          placeholder="Upload Trade License"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileChange(e, 'tlFile')
                          }
                          register={register}
                          allowedFileTypes={[
                            'image/png',
                            'image/jpeg',
                            'application/pdf',
                          ]}
                          maxFileSize={1024 * 1024}
                        />
                      ) : (
                        <PdfViewer
                          filePath={companyData?.tradeLicenseFilePath ?? ''}
                          fileLabel="Trade License"
                          label="View Trade License"
                          size="md"
                          className="mb-4"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 mb-6">
                  <div className="my-auto">
                    <LuCalendarClock className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                    <p className="mb-4 text-md">Trade License Expiry</p>
                  </div>
                  <div className="col-span-1 my-auto">
                    <DatePickerComponent
                      selectedDate={tradeLicenseExpiry}
                      onChange={setTradeLicenseExpiry}
                      isDateError={isDateError}
                      minDate={new Date()}
                      placeholder="MM/DD/YYYY"
                      errorText="Enter license expiry date"
                      className="flex-1 border-none"
                      disabled={!editState}
                    />
                  </div>
                </div>

                <Divider className="opacity-10" />

                {/* SIRA Certificate Section */}
                <div className="my-6">
                  <div className="grid grid-cols-4 mt-8 mb-6">
                    <div className="my-auto">
                      <TbFileCertificate className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">SIRA Certificate</p>
                    </div>
                    <div className="col-span-1 my-auto">
                      {editState ? (
                        <FileUpload
                          id="siraCertFile"
                          name="siraCertFile"
                          inputClassName="border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] !text-[var(--text-main)] !text-sm shadow-sm"
                          placeholder="Upload SIRA Certificate"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileChange(e, 'siraCertFile')
                          }
                          register={register}
                          allowedFileTypes={[
                            'image/png',
                            'image/jpeg',
                            'application/pdf',
                          ]}
                          maxFileSize={1024 * 1024}
                        />
                      ) : (
                        <PdfViewer
                          filePath={companyData?.siraCertFilePath ?? ''}
                          fileLabel="SIRA Certificate"
                          label="View Certificate"
                          size="md"
                          className="mb-4"
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4">
                    <div className="my-auto">
                      <LuCalendarClock className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">SIRA Certificate Expiry</p>
                    </div>
                    <div className="col-span-1 my-auto">
                      <DatePickerComponent
                        selectedDate={siraCertExpiry}
                        onChange={setSiraCertExpiry}
                        isDateError={isDateError}
                        minDate={new Date()}
                        placeholder="MM/DD/YYYY"
                        errorText="Enter license expiry date"
                        className="flex-1 border-none"
                        disabled={!editState}
                      />
                    </div>
                  </div>
                </div>

                <Divider className="opacity-10" />

                {/* Commercial Registration Section */}
                <div className="my-6">
                  <div className="grid grid-cols-4 mt-8 mb-6">
                    <div className="my-auto">
                      <TbFileCertificate className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">
                        Commercial Registration Certificate
                      </p>
                    </div>
                    <div className="col-span-1 my-auto">
                      {editState ? (
                        <FileUpload
                          id="commRegFile"
                          name="commRegFile"
                          inputClassName="border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] !text-[var(--text-main)] !text-sm shadow-sm"
                          placeholder="Upload Comm. Reg File"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileChange(e, 'commRegFile')
                          }
                          register={register}
                          allowedFileTypes={[
                            'image/png',
                            'image/jpeg',
                            'application/pdf',
                          ]}
                          maxFileSize={1024 * 1024}
                        />
                      ) : (
                        <PdfViewer
                          filePath={companyData?.commRegFilePath ?? ''}
                          fileLabel="Comm. Reg Certificate"
                          label="View Certificate"
                          size="md"
                          className="mb-4"
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4">
                    <div className="my-auto">
                      <LuCalendarClock className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">
                        Commercial Registration Expiry
                      </p>
                    </div>
                    <div className="col-span-1 my-auto">
                      <DatePickerComponent
                        selectedDate={commRegExpiry}
                        onChange={setCommRegExpiry}
                        isDateError={isDateError}
                        minDate={new Date()}
                        placeholder="MM/DD/YYYY"
                        errorText="Enter license expiry date"
                        className="flex-1 border-none"
                        disabled={!editState}
                      />
                    </div>
                  </div>
                </div>

                <Divider className="opacity-10" />

                {/* Insurance Section */}
                <div className="my-6">
                  <div className="grid grid-cols-4 mt-8 mb-6">
                    <div className="my-auto">
                      <TbLicense className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">Insurance</p>
                    </div>
                    <div className="col-span-1 my-auto">
                      {editState ? (
                        <FileUpload
                          id="insuranceFile"
                          name="insuranceFile"
                          inputClassName="border border-dashed border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] !text-[var(--text-main)] !text-sm shadow-sm"
                          placeholder="Upload Insurance File"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileChange(e, 'insuranceFile')
                          }
                          register={register}
                          allowedFileTypes={[
                            'image/png',
                            'image/jpeg',
                            'application/pdf',
                          ]}
                          maxFileSize={1024 * 1024}
                        />
                      ) : (
                        <PdfViewer
                          filePath={companyData?.insuranceFilePath ?? ''}
                          fileLabel="Insurance Certificate"
                          label="View Insurance"
                          size="md"
                          className="mb-4"
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4">
                    <div className="my-auto">
                      <LuCalendarClock className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                      <p className="mb-4 text-md">Insurance Expiry</p>
                    </div>
                    <div className="col-span-1 my-auto">
                      <DatePickerComponent
                        selectedDate={insuranceExpiry}
                        onChange={setInsuranceExpiry}
                        isDateError={isDateError}
                        minDate={new Date()}
                        placeholder="MM/DD/YYYY"
                        errorText="Enter license expiry date"
                        className="flex-1 border-none"
                        disabled={!editState}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <Divider className="opacity-10" />

            {/* Phone - only show if available */}
            {userProfileData?.phone && (
              <div className="grid grid-cols-4 mt-8">
                <div className="my-auto">
                  <MdLocalPhone className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                  <p className="mb-4 text-md">{t('profile_page.phone_no')}</p>
                </div>
                <div className="col-span-1 my-auto">
                  <Input
                    type="tel"
                    name="phone"
                    inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)] rtl:text-right ltr:text-left"
                    defaultValue={userProfileData?.phone ?? ''}
                    register={register}
                    disabled
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 my-6">
              <div className="my-auto">
                <IoMailOutline className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                <p className="mb-4 text-md">{t('profile_page.email')}</p>
              </div>
              <div className="col-span-1 my-auto">
                <Input
                  type="email"
                  name="email"
                  inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]"
                  defaultValue={userProfileData?.email ?? ''}
                  disabled
                />
              </div>
            </div>

            {/* <div className="grid grid-cols-4 my-6">
              <div className="my-auto">
                <Icon
                  icon="hugeicons:location-01"
                  width={22}
                  height={22}
                  className="my-auto ltr:float-left rtl:float-right me-3 text-white/[0.6]"
                />
                <p className="mb-4 text-md">{t('profile_page.address')}</p>
              </div>
              <div className="col-span-1 my-auto">
                <Input
                  type="text"
                  name="address"
                  inputClassName="shadow-sm border border-[var(--border-light)] rounded-lg placeholder:text-[var(--text-muted)]"
                  defaultValue={companyData?.address?.street ?? ''}
                  register={register}
                  disabled
                />
              </div>
            </div> */}

            <Divider className="opacity-10" />

            {/* Language Selection */}
            <div className="grid grid-cols-4 mt-8 mb-8">
              <div className="my-auto">
                <GrLanguage className="my-auto ltr:float-left rtl:float-right me-3 w-5 h-5 opacity-60" style={{ color: "var(--text-main)" }} />
                <p className="mb-2">{t('profile_page.language')}</p>
              </div>
              <div className="col-span-2 my-auto">
                {Object.keys(lngs).map((lng, index) => (
                  <Button
                    key={lng}
                    variant="shadow"
                    color="primary"
                    className={`text-white rounded-full shadow-lg ${index !== 0 && i18n.language === 'ar' ? 'ms-2' : 'me-3'
                      } ${i18n.resolvedLanguage === lng
                        ? 'font-bold border bg-[#04AA6D]'
                        : 'font-normal border bg-white/[0.04]'
                      }`}
                    type="button"
                    onPress={() => changeLanguage(lng)}
                  >
                    {(lngs as any)[lng].nativeName}
                  </Button>
                ))}
              </div>
            </div>
          </form>
        </section>
      </div>

      <CustomSnackbar
        open={openSnackbar}
        duration={3000}
        handleClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}
